import { API_CONFIG, getAuthToken } from '../config/api.config';
import { HttpError, ApiError } from '@/domain/entities';

export class HttpClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_CONFIG?.baseUrl || 'http://api-adm.iconlimits.store/';
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = getAuthToken();
    const isFormData = options.body instanceof FormData;

    const headers: HeadersInit = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 204) {
        return {} as T;
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const httpError = new HttpError(
          response.status,
          (data as ApiError).error || response.statusText,
          data as ApiError
        );
        console.error(`❌ HTTP Error [${response.status}] ${endpoint}:`, httpError.message, data);
        throw httpError;
      }

      return data as T;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof HttpError) {
        throw error;
      }
      if (error instanceof Error && error.name === 'AbortError') {
        console.error(`❌ Request timeout: ${endpoint}`);
        throw new HttpError(408, 'Request timeout');
      }
      console.error(`❌ Unknown error on ${endpoint}:`, error);
      throw new HttpError(500, error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    console.log(`🌐 GET ${endpoint}`);
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    console.log(`🌐 POST ${endpoint}`, data);
    const body = data instanceof FormData ? data : data ? JSON.stringify(data) : undefined;
    return this.request<T>(endpoint, {
      method: 'POST',
      body,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    console.log(`🌐 PUT ${endpoint}`, data);
    const body = data instanceof FormData ? data : data ? JSON.stringify(data) : undefined;
    return this.request<T>(endpoint, {
      method: 'PUT',
      body,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    console.log(`🌐 PATCH ${endpoint}`, data);
    const body = data instanceof FormData ? data : data ? JSON.stringify(data) : undefined;
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    console.log(`🌐 DELETE ${endpoint}`);
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const httpClient = new HttpClient();

